import { t } from '../config/i18n';
import { validationSchemaCO_Company, validationSchemaCO_LegalRepresentative } from '../pages/Referrals/Merchants/Olaclick/ValidationByCountry/ValidationsByCO';
import { validationSchemaPE_Company, validationSchemaPE_LegalRepresentative } from '../pages/Referrals/Merchants/Olaclick/ValidationByCountry/ValidationsByPE';
import { accountEditAbility, checkAllowedCapabilities } from './Capability';
import { defaultCountryTupay, SettlementsType, TopUpStatus } from './Constants';
import { statusMappingBatch, statusMappingOrder } from './Constants/PaymentOrdersConstants';
import { amountFormatter } from './Formatters/ListFormatters';
import { ApiDocumentationCountries } from './Paths';

export const countryCodeById = (countries, id, attrId = 'idCountry', attrIso = 'countryIso') => {
  const country = countries.filter((c) => c[attrId] === id);
  if (country.length && country[0][attrIso]) {
    return country[0][attrIso];
  }
  return id === -1 ? 'ALL' : '';
};

export const jsonFormatter = (json) => {
  return json && JSON.stringify(JSON.parse(json), null, 2);
};

export const isParamString = (data) => {
  if (data.indexOf('|') !== -1 && data.indexOf('=') === -1) return true;
  return false;
};

export const isValidJson = (json) => {
  try {
    JSON.parse(json);
  } catch (e) {
    return false;
  }
  return true;
};

export const urlDecode = (data) => {
  return data && decodeURIComponent(data.replace(/\+/g, ' '));
};

export const isXML = (data) => {
  const doc = new DOMParser().parseFromString(data, 'text/xml');
  return doc.body === null;
};

export const formatPaymentMethods = (paymentMethods) => {
  return paymentMethods.map((pm) => ({
    value: pm.key,
    label: `${pm.value} (${pm.key})`,
  }));
};

export const xmlFormatter = (data) => {
  const PADDING = ' '.repeat(2); // set desired indent size here
  const reg = /(>)(<)(\/*)/g;
  let pad = 0;
  const xml = data.replace(reg, '$1\r\n$2$3');

  return xml
    .split('\r\n')
    .map((node) => {
      let indent = 0;
      if (node.match(/.+<\/\w[^>]*>$/)) {
        indent = 0;
      } else if (node.match(/^<\/\w/) && pad > 0) {
        pad -= 1;
      } else if (node.match(/^<\w[^>]*[^/]>.*$/)) {
        indent = 1;
      } else {
        indent = 0;
      }
      pad += indent;

      return PADDING.repeat(pad - indent) + node;
    })
    .join('\r\n');
};

export const getOS = () => {
  const { userAgent, platform } = window.navigator;

  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

  let os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'Mac OS';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux';
  }

  return os;
};

export const getDecimalSeparator = (locale) => {
  const numberWithDecimalSeparator = 1.1;

  const separator = numberWithDecimalSeparator.toLocaleString(locale).substring(1, 2);
  return checkCorrectSeparator(separator) ? separator : '.';
};

export const getThousandSeparator = (locale) => {
  return getDecimalSeparator(locale) === '.' ? ',' : '.';
};

const checkCorrectSeparator = (separatorToCheck) => {
  return separatorToCheck === '.' || separatorToCheck === ',';
};
export const removeEmptyValues = (values, initialValues, removeExtraKeys = []) => {
  const removeKeys = Array.isArray(removeExtraKeys) ? removeExtraKeys : Object.keys(removeExtraKeys).reduce((ret, key) => [...ret, ...(removeExtraKeys[key].notValue ? [key] : [])], []);
  const acceptFields = Object.keys(initialValues).reduce((ret, key) => {
    return [...ret, ...(!removeKeys.includes(key) ? [key] : [])];
  }, []);
  return Object.keys(values).reduce((ret, key) => {
    if (Array.isArray(values[key]) && values[key].length === 0) {
      return {
        ...ret,
      };
    }
    return {
      ...ret,
      ...(acceptFields.includes(key) && values[key] && { [key]: values[key] }),
    };
  }, {});
};

export const checkboxesDefaultValues = (checkboxes) => {
  return Object.keys(checkboxes).reduce((ret, key) => {
    const { value = false } = checkboxes[key];
    return {
      ...ret,
      [key]: value,
    };
  }, {});
};

export const commercialCanEditUsers = () => {
  const { accountcommercialCanEditUsers, accountCreate, accountCreateAdmin } = accountEditAbility;
  return checkAllowedCapabilities(accountcommercialCanEditUsers) && !(checkAllowedCapabilities(accountCreate) || checkAllowedCapabilities(accountCreateAdmin));
};

export const getTimezoneOffsetHours = () => {
  return (new Date().getTimezoneOffset() * -1) / 60;
};

export const createAnchorDownloadFile = (data, filename) => {
  const blob = new Blob([data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
};

export const downloadPDF = (data, filename) => {
  const binaryString = window.atob(data);
  const binaryLen = binaryString.length;
  const bytes = new Uint8Array(binaryLen);
  for (let i = 0; i < binaryLen; i++) {
    const ascii = binaryString.charCodeAt(i);
    bytes[i] = ascii;
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

export const objectToOptions = (object, keyValue = '', keyLabel = '') => {
  return Object.keys(object).map((key) => {
    const value = keyValue ? object[key][keyValue] : key;
    const label = keyLabel ? object[key][keyLabel] : object[value];
    return {
      value,
      label,
    };
  });
};

export const serverObjectToOptions = (object, keyValue = '', keyLabel = '') => {
  return Object.keys(object).map((key) => {
    const value = keyValue ? object[key][keyValue] : key;
    const label = keyLabel ? object[key][keyLabel] : object[value];
    return {
      value,
      label: label.replace('_', ' '),
    };
  });
};

export const makeRequestSignUpRequestStatus = (status, balanceCurrency, referral, feeCurrency) => {
  const request = {
    status,
    balanceCurrency,
    referral,
    feeCurrency,
  };
  return request;
};

export const getCountryDocumentation = (region, country) => {
  return `${ApiDocumentationCountries}${`${region}/${country}#bank-codes`}`;
};

export const openUrlExternal = (url) => {
  window.open(url, '_blank');
};

export const getValidationSchema = (countryCode, t) => {
  // eslint-disable-next-line
  switch (countryCode) {
    case 'pe':
      return { STEP_1: validationSchemaPE_LegalRepresentative(t), STEP_2: validationSchemaPE_Company(t) };
    case 'co':
      return { STEP_1: validationSchemaCO_LegalRepresentative(t), STEP_2: validationSchemaCO_Company(t) };
  }
};

export const containsRole = (roles, role) => {
  if (Array.isArray(role)) {
    return role.some(role => roles.includes(role));
  }
  return roles.includes(role);
};

export function isValidSettlementForDownload({ type, status }) {
  return (
    (type === SettlementsType.SCHEDULED_BY_PERIOD ||
     type === SettlementsType.SETTLEMENT_BY_BUSINESS_DAYS) &&
    status === TopUpStatus.COMPLETED
  );
}

export const getFirstRowCsv = (rows, headers) => {
  if (rows.length > 0) { 
    const firstRow = rows[0].replace(/\r/g, '').split(',');

    const jsonObject = {};
    headers.forEach((header, index) => {
      jsonObject[t(header)] = firstRow[index] || '';
    });
    return jsonObject;
  }
  return null;
};


export const inferSeparator = (text) => {
  const separators = [',', ';', '\t', '|'];
  const counts = {};

  const rows = text.split('\n').slice(0, 10);

  rows.forEach(row => {
    separators.forEach(separator => {
      const count = (row.split(separator).length - 1);
      counts[separator] = (counts[separator] || 0) + count;
    });
  });

  return Object.keys(counts).reduce((a, b) => (counts[a] > counts[b] ? a : b));
};

export const getCsvInfo = async (file, csvHeaders) => {
  const amountIndex = 4;

  try {
    const text = await file.text();
    if (!text.trim()) {
      return { firstRow: {}, totalAmount: 0, totalRows: 0 };
    }

    const separator = inferSeparator(text);
    const rows = text.split('\n').slice(1);
    const filteredRows = rows.filter(row => row.trim() !== '');

    if (filteredRows.length === 0) {
      return { firstRow: {}, totalAmount: 0, totalRows: 0 };
    }

    const firstRow = getFirstRowCsv(filteredRows, csvHeaders); // Obtén la primera fila procesada
    const totalRows = filteredRows.length;

    const totalAmount = filteredRows.reduce((sum, row) => {
      const columns = row.split(separator);
      const amountValue = parseFloat(columns[amountIndex]);
      return !isNaN(amountValue) ? sum + amountValue : sum;
    }, 0);

    const amountWithFormat = amountFormatter(totalAmount, defaultCountryTupay.currencyCode);
    return { firstRow, totalAmount: amountWithFormat, totalRows };
  } catch (error) {
    console.error('Error processing CSV file:', error);
    return { firstRow: {}, totalAmount: 0, totalRows: 0 };
  }
};

export const mapPaymentOrderResponseStatus = (response) => {
  return {
    ...response,
    data: response.data.map(item => {
 return ({
      ...item,
      status: statusMappingOrder[item.status] || 'UNKNOWN'
    });
})
  };
};

export const mapPaymentBatchResponseStatus = (response) => {
  return {
    ...response,
    data: response.data.map(item => {
 return ({
      ...item,
      status: statusMappingBatch[item.status] || 'UNKNOWN'
    });
})
  };
};

export const hasPaymentOrderErrors = (orderData) => {
  return orderData && Array.isArray(orderData.errors) && orderData.errors.length > 0;
};

export const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
